import { apiGet, apiPost, apiPut, apiDelete, getMultipartAuthHeaders, API_BASE } from '@/utils/api';
import type {
  PayrollTableResponse,
  PayrollAdjustmentFull,
  InstitutionBankInfo,
  CreateInstitutionBankPayload,
  UpdateInstitutionBankPayload,
  PayrollBankAuthorization,
  StaffPaymentHistoryResponse,
  CreateAdjustmentPayload,
  GenerateBankAuthPayload,
  ConfirmPaymentPayload,
  BulkConfirmPayload,
  CreateSalaryConfigPayload,
  VacatairePayrollTableResponse,
  VacataireBankAuthorization,
  GenerateVacataireBankAuthPayload,
} from '@/types/payroll.types';

// ==================== Permanent Payroll Table ====================

export async function getPermanentPayrollTable(
  month: number,
  year: number,
  search?: string,
  campusId?: string,
): Promise<PayrollTableResponse> {
  const params = new URLSearchParams({ month: String(month), year: String(year) });
  if (search) params.set('search', search);
  if (campusId) params.set('campusId', campusId);

  return apiGet<PayrollTableResponse>(`/financial/payroll/permanent/table?${params}`);
}

// ==================== Adjustments ====================

export async function createAdjustment(data: CreateAdjustmentPayload): Promise<PayrollAdjustmentFull> {
  return apiPost<PayrollAdjustmentFull>('/financial/payroll/permanent/adjustments', data);
}

export async function getAdjustments(
  month: number,
  year: number,
  userId?: string,
): Promise<PayrollAdjustmentFull[]> {
  const params = new URLSearchParams({ month: String(month), year: String(year) });
  if (userId) params.set('userId', userId);

  return apiGet<PayrollAdjustmentFull[]>(`/financial/payroll/permanent/adjustments?${params}`);
}

// ==================== Payment Confirmation ====================

export async function confirmPayment(
  payslipId: string,
  data: ConfirmPaymentPayload = {},
): Promise<unknown> {
  return apiPost(`/financial/payroll/payslips/${payslipId}/confirm-payment`, data);
}

export async function bulkConfirmPayments(data: BulkConfirmPayload): Promise<{ confirmed: number }> {
  return apiPost<{ confirmed: number }>('/financial/payroll/payslips/bulk-confirm', data);
}

// ==================== Archive Staff ====================

export async function archiveStaffPayroll(salaryConfigId: string): Promise<unknown> {
  return apiPost(`/financial/payroll/salaries/${salaryConfigId}/archive`);
}

export async function archiveStaffFromPayroll(userId: string): Promise<unknown> {
  return apiPost('/financial/payroll/permanent/archive', { userId });
}

export async function unarchiveStaffFromPayroll(userId: string): Promise<unknown> {
  return apiPost('/financial/payroll/permanent/unarchive', { userId });
}

export async function getStaffPayrollArchiveStatus(
  userId: string,
): Promise<{ isArchived: boolean; archivedAt: string | null }> {
  return apiGet<{ isArchived: boolean; archivedAt: string | null }>(
    `/financial/payroll/permanent/archive-status/${userId}`,
  );
}

// ==================== Bank Authorization PDF ====================

export async function generateBankAuthorization(
  data: GenerateBankAuthPayload,
): Promise<PayrollBankAuthorization> {
  return apiPost<PayrollBankAuthorization>('/financial/payroll/permanent/bank-authorization', data);
}

export async function getBankAuthorizationHistory(
  month: number,
  year: number,
): Promise<PayrollBankAuthorization[]> {
  const params = new URLSearchParams({ month: String(month), year: String(year) });
  return apiGet<PayrollBankAuthorization[]>(
    `/financial/payroll/permanent/bank-authorization/history?${params}`,
  );
}

export async function downloadBankAuthorizationPdf(authorizationId: string): Promise<Blob> {
  const response = await fetch(
    `${API_BASE}/financial/payroll/permanent/bank-authorization/${authorizationId}/download`,
    {
      headers: getMultipartAuthHeaders(),
    },
  );
  if (!response.ok) throw new Error('Erreur lors du telechargement');
  return response.blob();
}

export async function confirmBankAuthorizationPayment(
  authorizationId: string,
  paymentReference?: string,
): Promise<PayrollBankAuthorization> {
  return apiPost<PayrollBankAuthorization>(
    `/financial/payroll/permanent/bank-authorization/${authorizationId}/confirm-payment`,
    { paymentReference },
  );
}

// ==================== Payslip Generation ====================

export async function generatePayslips(month: number, year: number): Promise<unknown> {
  // Get tenantId from localStorage for the request body
  const userStr = localStorage.getItem('nexedu_user');
  let tenantId: string | undefined;
  if (userStr) {
    try {
      const user = JSON.parse(userStr);
      tenantId = user?.tenantId;
    } catch {
      // Ignore JSON parse errors
    }
  }

  return apiPost('/financial/payroll/payslips/generate', { tenantId, month, year });
}

// ==================== Staff Own Payment History ====================

export async function getOwnPaymentHistory(
  page = 1,
  limit = 12,
): Promise<StaffPaymentHistoryResponse> {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  return apiGet<StaffPaymentHistoryResponse>(`/financial/payroll/my-payments?${params}`);
}

// ==================== Staff Salary Configuration ====================

export async function createSalaryConfig(data: CreateSalaryConfigPayload): Promise<unknown> {
  // Get tenantId from localStorage for the request body
  const userStr = localStorage.getItem('nexedu_user');
  let tenantId: string | undefined;
  if (userStr) {
    try {
      const user = JSON.parse(userStr);
      tenantId = user?.tenantId;
    } catch {
      // Ignore JSON parse errors
    }
  }

  return apiPost('/financial/payroll/salaries', { ...data, tenantId });
}

export async function getAllSalaryConfigs(): Promise<unknown[]> {
  return apiGet<unknown[]>('/financial/payroll/salaries');
}

// ==================== Institution Bank Info ====================

export async function getInstitutionBanks(): Promise<InstitutionBankInfo[]> {
  return apiGet<InstitutionBankInfo[]>('/institution-bank');
}

export async function getInstitutionBank(id: string): Promise<InstitutionBankInfo> {
  return apiGet<InstitutionBankInfo>(`/institution-bank/${id}`);
}

export async function createInstitutionBank(
  data: CreateInstitutionBankPayload,
): Promise<InstitutionBankInfo> {
  return apiPost<InstitutionBankInfo>('/institution-bank', data);
}

export async function updateInstitutionBank(
  id: string,
  data: UpdateInstitutionBankPayload,
): Promise<InstitutionBankInfo> {
  return apiPut<InstitutionBankInfo>(`/institution-bank/${id}`, data);
}

export async function deleteInstitutionBank(id: string): Promise<void> {
  await apiDelete(`/institution-bank/${id}`);
}

export interface BankCampusOption {
  id: string;
  name: string;
  isActive: boolean;
}

export interface InstitutionBankCampusOptions {
  /** Active campuses within the current user's scope: the only ones assignable to an account */
  assignable: BankCampusOption[];
  /** Every campus of the tenant, for resolving names on existing accounts */
  all: BankCampusOption[];
}

export async function getInstitutionBankCampuses(): Promise<InstitutionBankCampusOptions> {
  return apiGet<InstitutionBankCampusOptions>('/institution-bank/campuses');
}

// ==================== Vacataire Payroll ====================

export async function getVacatairePayrollTable(
  month: number,
  year: number,
  search?: string,
  campusId?: string,
): Promise<VacatairePayrollTableResponse> {
  const params = new URLSearchParams({ month: String(month), year: String(year) });
  if (search) params.set('search', search);
  if (campusId) params.set('campusId', campusId);

  return apiGet<VacatairePayrollTableResponse>(`/financial/payroll/vacataire/table?${params}`);
}

export async function generateVacataireBankAuthorization(
  data: GenerateVacataireBankAuthPayload,
): Promise<VacataireBankAuthorization> {
  return apiPost<VacataireBankAuthorization>('/financial/payroll/vacataire/bank-authorization', data);
}

export async function getVacataireBankAuthorizationHistory(
  month: number,
  year: number,
): Promise<VacataireBankAuthorization[]> {
  const params = new URLSearchParams({ month: String(month), year: String(year) });
  return apiGet<VacataireBankAuthorization[]>(
    `/financial/payroll/vacataire/bank-authorization/history?${params}`,
  );
}

export async function downloadVacataireBankAuthorizationPdf(authorizationId: string): Promise<Blob> {
  const response = await fetch(
    `${API_BASE}/financial/payroll/vacataire/bank-authorization/${authorizationId}/download`,
    {
      headers: getMultipartAuthHeaders(),
    },
  );
  if (!response.ok) throw new Error('Erreur lors du telechargement');
  return response.blob();
}

export async function confirmVacataireBankAuthorizationPayment(
  authorizationId: string,
  paymentReference?: string,
): Promise<VacataireBankAuthorization> {
  return apiPost<VacataireBankAuthorization>(
    `/financial/payroll/vacataire/bank-authorization/${authorizationId}/confirm-payment`,
    { paymentReference },
  );
}

// ==================== Vacataire Adjustments ====================

export async function createVacataireAdjustment(data: CreateAdjustmentPayload): Promise<PayrollAdjustmentFull> {
  return apiPost<PayrollAdjustmentFull>('/financial/payroll/vacataire/adjustments', data);
}

export async function getVacataireAdjustments(
  month: number,
  year: number,
  userId?: string,
): Promise<PayrollAdjustmentFull[]> {
  const params = new URLSearchParams({ month: String(month), year: String(year) });
  if (userId) params.set('userId', userId);

  return apiGet<PayrollAdjustmentFull[]>(`/financial/payroll/vacataire/adjustments?${params}`);
}
